/**
 * Module: screens/creator/CreatorOnboardingScreen
 * Role: Wizard root — routes between the 8 onboarding screens (FTR-1106
 *       amendment 2026-06-07, design Eqq6AbBPpm1zPPyErlkgJQ chat16):
 *       promo → url → selfie → consent → categories → formats →
 *       demographics → cities → (submit) → /creator-status.
 *
 *       BUG-1451 B5 (2026-08-20): легаси-шорткат УДАЛЁН. Он отправлял
 *       одобренного креатора без профиля (`creator_status=verified` +
 *       `creator_profile_complete=false`) сразу на `categories`, минуя `url`
 *       и `selfie`. Четырьмя экранами позже CitiesStep.handleSubmit слал
 *       `selfie_base64: ''` без ссылки на соцсеть, бэкенд отвечал 422
 *       по-английски, а фильтр BUG-1153 (кириллица-только) превращал это в
 *       «Не удалось отправить заявку. Попробуйте ещё раз.» — бесконечный круг.
 *       Затронутых людей на проде на момент удаления: 0. Замена —
 *       `requiredStepBefore()`: неполный черновик возвращается на тот экран,
 *       которого ему не хватает, вместо тупика.
 *
 * Uses:
 *   - react-router-dom (Routes, Route, Navigate)
 *   - screens/creator/store:useWizardStore, WizardDraft
 *   - screens/creator/steps/*
 *
 * Used by: App.tsx route /creator-onboarding/*
 * Glossary: ai/glossary/buyer.md
 */

import type { ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useWizardStore, type WizardDraft } from './store';
import PromoScreen from './steps/PromoScreen';
import UrlStep from './steps/UrlStep';
import SelfieStep from './steps/SelfieStep';
import ConsentStep from './steps/ConsentStep';
import CategoriesStep from './steps/CategoriesStep';
import FormatsStep from './steps/FormatsStep';
import DemographicsStep from './steps/DemographicsStep';
import CitiesStep from './steps/CitiesStep';

/**
 * Какой из ранних шагов обязан быть пройден, чтобы шаги профиля
 * (categories/formats/demographics/cities) вели к УСПЕШНОЙ подаче — или `null`,
 * если в черновике уже есть всё, чего требует бэкенд.
 *
 * SYMMETRIC — зеркалит серверный контракт POST /creator/verify_apply:
 * `CreatorVerifyRequest.selfie_base64` объявлен как `Field(..., min_length=1)`,
 * и требуется хотя бы одна из ссылок vk_url / tiktok_url
 * (src/api/v2/buyer/_creator_schemas.py + creator_verification.py).
 */
export function requiredStepBefore(draft: WizardDraft): 'url' | 'selfie' | null {
  if (!draft.vk_url && !draft.tiktok_url) return 'url';
  if (!draft.selfie_dataUrl) return 'selfie';
  return null;
}

/** Уводит неполный черновик на недостающий шаг вместо круга с 422. */
function ProfileStepGuard({ children }: { children: ReactElement }): ReactElement {
  const { draft } = useWizardStore();
  const missing = requiredStepBefore(draft);
  if (missing !== null) return <Navigate to={`../${missing}`} replace />;
  return children;
}

export default function CreatorOnboardingScreen() {
  return (
    <Routes>
      {/* FTR-1131 amendment 2026-06-07: promo is the wizard entry point.
          Home/Tasks/Profile promo cards route here (not directly to /url). */}
      <Route index element={<Navigate to="promo" replace />} />
      <Route path="promo" element={<PromoScreen />} />
      <Route path="url" element={<UrlStep />} />
      <Route path="selfie" element={<SelfieStep />} />
      <Route path="consent" element={<ConsentStep />} />
      {/* BUG-1451 B5: шаги профиля доступны только с подаваемым черновиком. */}
      <Route path="categories" element={<ProfileStepGuard><CategoriesStep /></ProfileStepGuard>} />
      <Route path="formats" element={<ProfileStepGuard><FormatsStep /></ProfileStepGuard>} />
      <Route path="demographics" element={<ProfileStepGuard><DemographicsStep /></ProfileStepGuard>} />
      <Route path="cities" element={<ProfileStepGuard><CitiesStep /></ProfileStepGuard>} />
      {/* Pre-2026-06-07 routes: keep old deep-links out of a 404.
          BUG-1451 B5: `profile` раньше вёл на `../categories` — в середину
          мастера, то есть ровно в тупик. Теперь начинает поток с начала. */}
      <Route path="screenshot" element={<Navigate to="../consent" replace />} />
      <Route path="profile" element={<Navigate to="../url" replace />} />
      <Route path="*" element={<Navigate to="promo" replace />} />
    </Routes>
  );
}
